import React, { useMemo } from 'react';
import { useRoute, type RouteProp } from '@react-navigation/native';
import { Platform, View } from 'react-native';
import { Text, InsuranceCompanyLogo } from '~/components';
import type { RootStackParamList } from '~/navigation/types';

import Item from './components/Item';
import { formatMoney } from '~/utils';
import DefaultContainer from '../../components/DefaultContainer';
import { useUserInfoStore } from '~/stores/userInfo';

const Separator = () => <View className="h-[1px] bg-gray-100 mb-6" />;

const HiredInsuranceCoverageAuto: React.FC = () => {
  const contractedServices = useUserInfoStore(
    (state) => state.user?.contractedServices
  );

  const {
    params: { id: certificateCode },
  } =
    useRoute<
      RouteProp<RootStackParamList, 'MarisaServicos_HiredInsuranceCoverageAuto'>
    >();

  const contractedService = contractedServices?.find(
    (service) => service.certificateCode === certificateCode
  );

  const vehicleInfo = useMemo(
    () => contractedService?.vehicle,
    [contractedService?.vehicle]
  );

  if (!contractedService) return null;

  return (
    <DefaultContainer type="marisa-auto" className="pt-6 pb-12">
      <Text className="text-base text-gray-600">Coberturas do Seguro</Text>
      <Text className="mb-4 text-xs color-primary-600">Seguro Auto</Text>

      {!!contractedService.chosenInstallmentValue && (
        <View className="flex-row justify-between border px-4 py-5 rounded-xl border-primary-600 items-center">
          <Text className="color-primary-600 text-xs">Valor do seguro</Text>
          <Text className="color-primary-600 text-sm">
            R${' '}
            <Text className="text-xl font-medium">
              {formatMoney(contractedService.chosenInstallmentValue)}
            </Text>
            /mês
          </Text>
        </View>
      )}

      {vehicleInfo && (
        <Text className="my-6 font-medium text-sm text-primary-600">
          {vehicleInfo?.manufacturer} - {vehicleInfo?.model} -{' '}
          {vehicleInfo?.yearModel} / {vehicleInfo?.manufactureYear}
        </Text>
      )}

      <View className="h-[1px] bg-gray-100 mb-6" />

      <Item
        label="Valor da tabela FIPE"
        value={formatMoney(contractedService.plan?.fipeValue ?? 0, true)}
      />

      <Separator />

      <View className="mb-4">
        {contractedService.plan?.details.map((detail, index) => (
          <React.Fragment key={detail.description}>
            <Item label={detail.description} value={detail.value} />
            {index !== (contractedService.plan?.details?.length || 0) - 1 && (
              <Separator />
            )}
          </React.Fragment>
        ))}
      </View>

      {/* <Components.ResumeCard data={contractedService} /> */}

      <View
        className={`rounded-md border border-primary-600 p-4 shadow bg-light-100 ${
          Platform.OS === 'android' ? 'shadow-black/40' : ''
        }`}
      >
        <Text className="font-medium text-sm">
          {contractedService.plan?.name}
        </Text>

        <View className="flex-row items-center justify-between mt-4">
          <Text className="text-xs color-gray-400">Valor da Franquia: </Text>
          <Text className="text-xs color-primary-600 font-medium">
            {contractedService.plan?.franchiseValue}
          </Text>
        </View>
      </View>

      <Text className="text-center text-xs mt-6 mb-2 color-gray-400">
        Seguro administrado por:
      </Text>

      <View className="items-center">
        <InsuranceCompanyLogo
          name={contractedService.insuranceCompanyName}
          height={80}
          width={80}
        />
      </View>
    </DefaultContainer>
  );
};

export default HiredInsuranceCoverageAuto;
